/**
 * Shared ledger-history list (production-records R0). The shelf ledgers (stock items, printed
 * transfers) always recorded who/when/what — the UI never called their detail endpoints, so an
 * investigation had nowhere to look. Presentational only: each host page fetches its own detail
 * endpoint and passes the movements through; draw rows link back to the order they served.
 */
import { Link } from '@tanstack/react-router';
import { formatStamp, orderCardSearch } from './order-detail-lib';

export type MovementRow = {
  id: string;
  type: string;
  qtyDelta: number;
  createdAt: string;
  actor?: string;
  reason?: string;
  countedQty?: number;
  orderRef?: { storeId: string; orderId: string; lineId: string };
};

/** Newest first, capped like the blanks dialog — history beyond this lives in Export. */
const MAX_ROWS = 20;

export function MovementList({ rows, emptyText }: { rows: MovementRow[]; emptyText: string }) {
  if (rows.length === 0) {
    return <p className="py-4 text-center text-sm text-muted-foreground">{emptyText}</p>;
  }
  const visible = [...rows]
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    .slice(0, MAX_ROWS);
  return (
    <ul className="divide-y text-sm">
      {visible.map((m) => (
        <li key={m.id} className="flex flex-wrap items-center justify-between gap-x-3 py-2">
          <div className="min-w-0">
            <p className="font-medium">
              {m.type}
              <span
                className={
                  m.qtyDelta < 0
                    ? 'ml-2 tabular-nums text-red-700 dark:text-red-400'
                    : 'ml-2 tabular-nums text-emerald-700 dark:text-emerald-400'
                }
              >
                {m.qtyDelta > 0 ? `+${m.qtyDelta}` : m.qtyDelta}
              </span>
              {m.countedQty != null && (
                <span className="ml-2 text-xs text-muted-foreground">→ {m.countedQty}</span>
              )}
            </p>
            <p className="text-xs text-muted-foreground">
              {formatStamp(m.createdAt, m.actor)}
              {m.reason ? ` · ${m.reason}` : ''}
            </p>
          </div>
          {m.orderRef && (
            <Link
              to="/orders/$storeId/$orderId"
              params={{ storeId: m.orderRef.storeId, orderId: m.orderRef.orderId }}
              search={orderCardSearch('inventory')}
              className="text-xs underline underline-offset-2 hover:no-underline"
              title="Open the order this draw served"
            >
              order ↗
            </Link>
          )}
        </li>
      ))}
      {rows.length > MAX_ROWS && (
        <li className="py-2 text-xs text-muted-foreground">
          Showing the latest {MAX_ROWS} of {rows.length} — the full ledger is in Export.
        </li>
      )}
    </ul>
  );
}
